import React, { useCallback, useEffect, useState } from "react";
import { differenceInMinutes, parse } from "date-fns";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Switch, Typography, Input, Box, FormControl, Select, Option, Card,
  Avatar, Menu, MenuItem, IconButton
} from "@mui/joy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";
import { supabase } from "../../supabaseClient";
import { Teacher, ClassSchedule } from "../../types";
import { fetchProfilePicture, getInitials } from "../../utils";

// Define months and other constants
const months = [
  { label: "January (1)", value: 1 },
  { label: "February (2)", value: 2 },
  { label: "March (3)", value: 3 },
  { label: "April (4)", value: 4 },
  { label: "May (5)", value: 5 },
  { label: "June (6)", value: 6 },
  { label: "July (7)", value: 7 },
  { label: "August (8)", value: 8 },
  { label: "September (9)", value: 9 },
  { label: "October (10)", value: 10 },
  { label: "November (11)", value: 11 },
  { label: "December (12)", value: 12 },
];

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const yearsRange = Array.from({ length: 9 }, (_, i) => {
  const currentYear = new Date().getFullYear();
  return currentYear - 4 + i;
});

// Validation schema using Yup
const classSchema = yup.object().shape({
  primary_teacher_id: yup.string().nullable().required(),
  secondary_teacher_id: yup.string().nullable().optional(),
  start_month: yup.number().required(),
  start_year: yup.number().required(),
  end_month: yup.number().required(),
  end_year: yup.number().required(),
  schedule: yup.object().shape({
    monday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    }),
    tuesday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    }),
    wednesday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    }),
    thursday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    }),
    friday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    }),
    saturday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    }),
    sunday: yup.object().shape({
      start: yup.string().nullable(),
      end: yup.string().nullable(),
      halfEnd: yup.string().nullable(),
      hasSchool: yup.boolean(),
      supportsHalf: yup.boolean()
    })
  })
}) as yup.ObjectSchema<ClassFormValues>;

const calculateDuration = (start: string | null, end: string | null): string => {
  if (!start || !end) return "0 min";

  const format = "HH:mm";
  const startTime = parse(start, format, new Date());
  const endTime = parse(end, format, new Date());

  const diffMinutes = differenceInMinutes(endTime, startTime);

  if (diffMinutes <= 0) return "0 min";

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return hours > 0 ? `${hours} hour${hours === 1 ? "" : "s"} ${minutes > 0 ? `${minutes} minute${minutes === 1 ? "" : "s"}` : ""}` : `${minutes} minute${minutes === 1 ? "" : "s"}`;
};

export interface ScheduleDay {
  start?: string | null;
  end?: string | null;
  halfEnd?: string | null;
  hasSchool?: boolean;
  supportsHalf?: boolean;
}

interface ClassFormValues {
  primary_teacher_id?: string | null;
  secondary_teacher_id?: string | null;
  start_month: number;
  start_year: number;
  end_month: number;
  end_year: number;
  schedule: ClassSchedule;
}

interface AddEditClassModalProps {
  classData?: {
    id?: string;
    primary_teacher_id?: string | null;
    secondary_teacher_id?: string | null;
    start_month?: string | number;
    start_year?: string | number;
    end_month?: string | number;
    end_year?: string | number;
    schedule?: {
      monday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
      tuesday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
      wednesday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
      thursday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
      friday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
      saturday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
      sunday: { start_time?: string | null; end_time?: string | null; hasSchool?: boolean };
    };
  };
}

const AddEditClassModal: React.FC<AddEditClassModalProps> = ({ classData }) => {
  const { closeModal, setModalTitle, registerSubmitCallback, setCanSubmit } = useModal();
  const { showToast } = useToast();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [copyMenuAnchor, setCopyMenuAnchor] = useState<null | HTMLElement>(null);
  const [copyMenuTarget, setCopyMenuTarget] = useState<string | null>(null);
  const [endDateManuallySet, setEndDateManuallySet] = useState(false);
  // Initialize globalSupportsHalf - will be updated in useEffect when classData loads
  const [globalSupportsHalf, setGlobalSupportsHalf] = useState(false);

  // Helper to add months to a date
  function addMonths(month: number, year: number, monthsToAdd: number) {
    let newMonth = month + monthsToAdd;
    let newYear = year;
    while (newMonth > 12) {
      newMonth -= 12;
      newYear += 1;
    }
    return { month: newMonth, year: newYear };
  }

  // Define default values
  const isNewClass = !classData?.id;
  const startMonthDefault = classData?.start_month ? Number(classData.start_month) : new Date().getMonth() + 1;
  const startYearDefault = classData?.start_year ? Number(classData.start_year) : new Date().getFullYear();
  let endMonthDefault, endYearDefault;
  if (classData?.end_month && classData?.end_year) {
    endMonthDefault = Number(classData.end_month);
    endYearDefault = Number(classData.end_year);
  } else if (isNewClass) {
    const end = addMonths(startMonthDefault, startYearDefault, 9);
    endMonthDefault = end.month;
    endYearDefault = end.year;
  } else {
    endMonthDefault = startMonthDefault;
    endYearDefault = startYearDefault;
  }

  const defaultValues: ClassFormValues = {
    primary_teacher_id: classData?.primary_teacher_id || null,
    secondary_teacher_id: classData?.secondary_teacher_id || null,
    start_month: startMonthDefault,
    start_year: startYearDefault,
    end_month: endMonthDefault,
    end_year: endYearDefault,
    schedule: (classData?.schedule as ClassSchedule) || {
      monday: { start: "", end: "", halfEnd: null, hasSchool: true, supportsHalf: false },
      tuesday: { start: "", end: "", halfEnd: null, hasSchool: true, supportsHalf: false },
      wednesday: { start: "", end: "", halfEnd: null, hasSchool: true, supportsHalf: false },
      thursday: { start: "", end: "", halfEnd: null, hasSchool: true, supportsHalf: false },
      friday: { start: "", end: "", halfEnd: null, hasSchool: true, supportsHalf: false },
      saturday: { start: "", end: "", halfEnd: null, hasSchool: false, supportsHalf: false },
      sunday: { start: "", end: "", halfEnd: null, hasSchool: false, supportsHalf: false }
    }
  };

  // console.log("Default Values: ", defaultValues);

  // Initialize useForm
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<ClassFormValues>({
    defaultValues,
    resolver: yupResolver(classSchema),
    mode: "onTouched", // Validate only after a field is touched
    reValidateMode: "onBlur", // Re-validate when the field loses focus
  });

  const schedule = watch("schedule");

  // Define your onSubmit function
  const onSubmit = useCallback(
    async (data: ClassFormValues) => {
      // console.log("Submitting data", data);

      // Convert empty strings to nulls for teacher IDs
      if (data.primary_teacher_id === "") {
        data.primary_teacher_id = null;
      }
      if (data.secondary_teacher_id === "") {
        data.secondary_teacher_id = null;
      }

      // Prepare the data (no longer need global half-day fields)
      const submitData = {
        ...data,
      };

      try {
        let error;

        if (classData?.id) {
          // Update existing class if ID is provided
          ({ error } = await supabase
            .from("classes")
            .update(submitData)
            .eq("id", classData.id)
            .select());
        } else {
          // Insert new class if no ID is provided
          ({ error } = await supabase
            .from("classes")
            .insert([submitData])
            .select());
        }

        if (error) throw error;

        showToast("Class saved successfully!", "success", "solid");
        closeModal();
      } catch (error) {
        showToast("Error saving class.", "danger", "solid");
      }
    },
    [showToast, closeModal, classData]
  );

  // Fetch teachers from Supabase
  useEffect(() => {
    setModalTitle(classData ? "Edit Class" : "Add Class");

    const fetchTeachers = async () => {
      const { data, error } = await supabase.from("teachers").select("*");
      if (error) {
        showToast("Failed to load teachers", "danger", "solid");
      } else {
        // Fetch profile pictures for each teacher
        const teachersWithPictures = await Promise.all(
          data.map(async (teacher) => {
            const profilePicture = await fetchProfilePicture("teachers", teacher.profile_picture_id);

            return {
              ...teacher,
              profile_picture: profilePicture,
            };
          })
        );
        setTeachers(teachersWithPictures);
      }
    };

    fetchTeachers();

    registerSubmitCallback(async () => {
      await handleSubmit(onSubmit)(); // Execute form submission
    });
    /* eslint-disable-next-line */
  }, []);

  // Set default form values after teachers load otherwise they won't be selected.
  useEffect(() => {
    if (teachers.length > 0 && classData) {
      // Set the default primary and secondary teacher IDs once teachers are loaded
      setValue("primary_teacher_id", classData.primary_teacher_id || "");
      setValue("secondary_teacher_id", classData.secondary_teacher_id || "");
    }
  }, [teachers, classData, setValue]);

  // Initialize globalSupportsHalf based on classData when it loads
  useEffect(() => {
    if (classData?.schedule) {
      // Check if any day in the existing schedule supports half day
      const schedule = classData.schedule as ClassSchedule;
      const hasHalfDaySupport = Object.values(schedule).some(day => {
        return day.supportsHalf === true;
      });

      setGlobalSupportsHalf(hasHalfDaySupport);
    }
  }, [classData]);

  // Control Save button state based on form validation
  useEffect(() => {
    // Check for half-day validation errors across all schedule days
    const hasHalfDayErrors = schedule && Object.values(schedule).some((day) => {
      if (day.supportsHalf && day.halfEnd && day.end) {
        // Parse times and compare
        const halfEndTime = parse(day.halfEnd, 'HH:mm', new Date());
        const fullEndTime = parse(day.end, 'HH:mm', new Date());
        return halfEndTime >= fullEndTime;
      }
      return false;
    });

    // For new classes, require all fields to be valid
    // For existing classes, only require form to be valid if it's been modified
    const shouldEnableSubmit = classData?.id 
      ? isValid && !hasHalfDayErrors // For editing: form must be valid
      : isValid && isDirty && !hasHalfDayErrors; // For new: form must be valid and have been touched
    
    setCanSubmit(shouldEnableSubmit);
  }, [isValid, isDirty, setCanSubmit, classData?.id, schedule]);

  const selectedPrimaryTeacher = watch("primary_teacher_id");
  const selectedSecondaryTeacher = watch("secondary_teacher_id");

  const handleStartMonthChange = (month: number) => {
    setValue("start_month", month);
    if (!endDateManuallySet && isNewClass) {
      const startYear = watch("start_year");
      const end = addMonths(month, startYear, 9);
      setValue("end_month", end.month);
      setValue("end_year", end.year);
    } else if (watch("start_year") === watch("end_year") && watch("end_month") <= month) {
      setValue("end_month", month + 1); // Ensure end month is greater if same year
    }
  };

  const handleEndMonthChange = (month: number) => {
    setEndDateManuallySet(true);
    if (watch("start_year") === watch("end_year") && month <= watch("start_month")) {
      setValue("end_month", watch("start_month") + 1); // Prevent end month being earlier if same year
    } else {
      setValue("end_month", month);
    }
  };

  const handleStartYearChange = (year: number) => {
    setValue("start_year", year);
    if (!endDateManuallySet && isNewClass) {
      const startMonth = watch("start_month");
      const end = addMonths(startMonth, year, 9);
      setValue("end_month", end.month);
      setValue("end_year", end.year);
    } else if (year > watch("end_year")) {
      setValue("end_year", year); // Ensure end year is greater or equal to start year
    }
  };

  const handleEndYearChange = (year: number) => {
    setEndDateManuallySet(true);
    if (year < watch("start_year")) {
      setValue("end_year", watch("start_year")); // Prevent end year from being less than start year
    } else {
      setValue("end_year", year);
    }
  };

  // Copy schedule times from one day to another
  const copyScheduleTimes = (fromDay: string, toDay: string) => {
    const fromDayKey = fromDay.toLowerCase() as keyof ClassFormValues["schedule"];
    const toDayKey = toDay.toLowerCase() as keyof ClassFormValues["schedule"];
    
    const fromSchedule = schedule[fromDayKey];
    
    // Copy the hasSchool state and times
    setValue(`schedule.${toDayKey}.hasSchool`, fromSchedule.hasSchool);
    setValue(`schedule.${toDayKey}.start`, fromSchedule.start || "");
    setValue(`schedule.${toDayKey}.end`, fromSchedule.end || "");
    
    // Copy half-day settings if they exist
    setValue(`schedule.${toDayKey}.supportsHalf` as any, (fromSchedule as any).supportsHalf || false);
    setValue(`schedule.${toDayKey}.halfEnd` as any, (fromSchedule as any).halfEnd || null);
    
    showToast(`Copied ${fromDay} schedule to ${toDay}`, "success", "solid");
  };

  // Handle copy menu open
  const handleCopyMenuOpen = (event: React.MouseEvent<HTMLElement>, targetDay: string) => {
    setCopyMenuAnchor(event.currentTarget);
    setCopyMenuTarget(targetDay);
  };

  // Handle copy menu close
  const handleCopyMenuClose = () => {
    setCopyMenuAnchor(null);
    setCopyMenuTarget(null);
  };

  // Handle copy from specific day
  const handleCopyFromDay = (fromDay: string) => {
    if (copyMenuTarget) {
      copyScheduleTimes(fromDay, copyMenuTarget);
    }
    handleCopyMenuClose();
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <form>
        {/* Teacher Selection */}
        <Typography level="title-lg">Teachers</Typography>
        <Box display="flex" gap={2} sx={{ width: "100%" }}>
          <FormControl sx={{ flex: 1 }}>
            <Typography>Primary Teacher</Typography>
            <Controller
              name="primary_teacher_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value || ""}
                  onChange={(event, newValue) => field.onChange(newValue)}
                  placeholder="Select a primary teacher..."
                  renderValue={(selected) => {
                    if (!selected || !selected?.value) {
                      return "None";
                    }

                    const selectedTeacher = teachers.find((teacher) => teacher.id === (selected?.value ?? ""));
                    return selectedTeacher ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={selectedTeacher.profile_picture || ""}
                          alt={getInitials(selectedTeacher)}
                          sx={{ width: 24, height: 24 }}
                        >
                          {!selectedTeacher.profile_picture && getInitials(selectedTeacher)}
                        </Avatar>
                        <Typography>
                          {selectedTeacher.first_name} {selectedTeacher.last_name}
                        </Typography>
                      </Box>
                    ) : (
                      "None"
                    );
                  }}
                >
                  <Option value="">None</Option>
                  {teachers
                    .filter((teacher) => teacher.id !== selectedSecondaryTeacher) // Exclude selected secondary teacher
                    .map((teacher) => (
                      <Option key={teacher.id} value={teacher.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {teacher.profile_picture && (
                            <img
                              src={teacher.profile_picture}
                              alt={getInitials(teacher)}
                              style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }}
                            />
                          )}
                          <Typography>
                            {teacher.first_name} {teacher.last_name}
                          </Typography>
                        </Box>
                      </Option>
                    ))}
                </Select>
              )}
            />
            {errors.primary_teacher_id && <Typography>{errors.primary_teacher_id.message}</Typography>}
          </FormControl>

          <FormControl sx={{ flex: 1 }}>
            <Typography>Secondary Teacher</Typography>
            <Controller
              control={control}
              name="secondary_teacher_id"
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value || ""}
                  onChange={(event, newValue) => field.onChange(newValue)}
                  placeholder="Select a secondary teacher..."
                  renderValue={(selected) => {
                    if (!selected || !selected?.value) {
                      return "None";
                    }

                    const selectedTeacher = teachers.find((teacher) => teacher.id === (selected?.value ?? ""));
                    return selectedTeacher ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={selectedTeacher.profile_picture || ""}
                          alt={getInitials(selectedTeacher)}
                          sx={{ width: 24, height: 24 }}
                        >
                          {!selectedTeacher.profile_picture && getInitials(selectedTeacher)}
                        </Avatar>
                        <Typography>
                          {selectedTeacher.first_name} {selectedTeacher.last_name}
                        </Typography>
                      </Box>
                    ) : (
                      "None"
                    );
                  }}
                >
                  <Option value="">None</Option>
                  {teachers
                    .filter((teacher) => teacher.id !== selectedPrimaryTeacher) // Exclude selected primary teacher
                    .map((teacher) => (
                      <Option key={teacher.id} value={teacher.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {teacher.profile_picture && (
                            <img
                              src={teacher.profile_picture}
                              alt={getInitials(teacher)}
                              style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }}
                            />
                          )}
                          <Typography>
                            {teacher.first_name} {teacher.last_name}
                          </Typography>
                        </Box>
                      </Option>
                    ))}
                </Select>
              )}
            />
            {errors.secondary_teacher_id && <Typography>{errors.secondary_teacher_id.message}</Typography>}
          </FormControl>
        </Box>

        <Typography level="title-lg" sx={{ mt: 3 }}>Dates</Typography>
        <Box display="flex" gap={2} sx={{ flexWrap: "wrap", justifyContent: "space-between" }}>
          <FormControl sx={{ width: "calc(50% - 8px)" }}>
            <Typography>Start Month</Typography>
            <Controller
              control={control}
              name="start_month"
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={(_, value) => handleStartMonthChange(value || 1)}
                >
                  {months.map(({ label, value }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.start_month && <Typography>{errors.start_month.message}</Typography>}
          </FormControl>

          <FormControl sx={{ width: "calc(50% - 8px)" }}>
            <Typography>Start Year</Typography>
            <Controller
              control={control}
              name="start_year"
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={(_, value) => handleStartYearChange(value || new Date().getFullYear())}
                >
                  {yearsRange.map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.start_year && <Typography>{errors.start_year.message}</Typography>}
          </FormControl>
        </Box>

        <Box display="flex" gap={2} sx={{ flexWrap: "wrap", justifyContent: "space-between" }}>
          <FormControl sx={{ width: "calc(50% - 8px)" }}>
            <Typography>End Month</Typography>
            <Controller
              control={control}
              name="end_month"
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={(_, value) => handleEndMonthChange(value || 1)}
                >
                  {months.map(({ label, value }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.end_month && <Typography>{errors.end_month.message}</Typography>}
          </FormControl>

          <FormControl sx={{ width: "calc(50% - 8px)" }}>
            <Typography>End Year</Typography>
            <Controller
              control={control}
              name="end_year"
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={(_, value) => handleEndYearChange(value || new Date().getFullYear() + 1)}
                >
                  {yearsRange.map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.end_year && <Typography>{errors.end_year.message}</Typography>}
          </FormControl>
        </Box>

        <Typography level="title-lg" sx={{ mt: 3 }}>Half-Day Options</Typography>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <Switch
            checked={globalSupportsHalf}
            onChange={(event) => {
              setGlobalSupportsHalf(event.target.checked);
              // When toggling off, clear all half-day settings from schedule
              if (!event.target.checked) {
                daysOfWeek.forEach((day) => {
                  const dayKey = day.toLowerCase() as keyof ClassFormValues["schedule"];
                  setValue(`schedule.${dayKey}.supportsHalf` as any, false);
                  setValue(`schedule.${dayKey}.halfEnd` as any, null);
                });
              }
            }}
          />
          <Typography>Support Half-Day Enrollments</Typography>
        </Box>

        <Typography level="title-lg" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>Weekly Schedule</Typography>
        {daysOfWeek.map((day) => {
          const dayKey = day.toLowerCase() as keyof ClassFormValues["schedule"]; // Explicit key mapping
          
          return (
            <Card 
              key={day} 
              variant="outlined" 
              sx={{ 
                mb: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: 'sm',
                  borderColor: 'primary.300'
                }
              }}
            >
              <Box display="flex" flexDirection="column" gap={2} padding={2}>
                {/* First row - Day toggle, start time, end time, duration, copy */}
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={2}
                  sx={{
                    py: 0.5,
                    '&:hover': {
                      bgcolor: 'background.level1',
                      borderRadius: 'sm'
                    }
                  }}
                >
                  <Controller
                    control={control}
                    name={`schedule.${dayKey}.hasSchool`}
                    render={({ field }) => (
                      <FormControl sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography component="label" sx={{ display: "flex", alignItems: "center" }}>
                          <Switch
                            {...field}
                            checked={!!field.value} // Ensure it"s always a boolean
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              field.onChange(isChecked); // Update the field value with the boolean
                              if (!isChecked) {
                                // When turning off hasSchool, clear all times
                                setValue(`schedule.${dayKey}.start`, "");
                                setValue(`schedule.${dayKey}.end`, "");
                                setValue(`schedule.${dayKey}.supportsHalf` as any, false);
                                setValue(`schedule.${dayKey}.halfEnd` as any, null);
                              } else {
                                // When turning on hasSchool, provide default times if none exist
                                const currentStart = schedule[dayKey].start;
                                const currentEnd = schedule[dayKey].end;
                                if ((!currentStart || currentStart.trim() === '') && 
                                    (!currentEnd || currentEnd.trim() === '')) {
                                  setValue(`schedule.${dayKey}.start`, "08:00");
                                  setValue(`schedule.${dayKey}.end`, "15:00");
                                }
                              }
                            }}
                            sx={{ mr: 1 }}
                          />
                          {day}
                        </Typography>
                      </FormControl>
                    )}
                  />

                  <Controller
                    control={control}
                    name={`schedule.${dayKey}.start`}
                    render={({ field }) => (
                      <Input
                        type="time"
                        disabled={!schedule[dayKey].hasSchool}
                        value={field.value || ""}
                        onChange={(e) => {
                          const newStartValue = e.target.value;
                          field.onChange(newStartValue);
                          
                          // Auto-correction: If both start and end are empty, set hasSchool to false
                          const currentEnd = schedule[dayKey].end;
                          if ((!newStartValue || newStartValue.trim() === '') && 
                              (!currentEnd || currentEnd.trim() === '')) {
                            setValue(`schedule.${dayKey}.hasSchool`, false);
                          }
                        }}
                        placeholder="Start time"
                        sx={{ 
                          minWidth: 120,
                          '& input:placeholder-shown': {
                            color: 'var(--joy-palette-text-tertiary)'
                          }
                        }}
                      />
                    )}
                  />
                  <Typography>to</Typography>
                  
                  <Controller
                    control={control}
                    name={`schedule.${dayKey}.end`}
                    render={({ field }) => (
                      <Input
                        type="time"
                        disabled={!schedule[dayKey].hasSchool}
                        value={field.value || ""}
                        onChange={(e) => {
                          const newEndValue = e.target.value;
                          field.onChange(newEndValue);
                          
                          // Auto-correction: If both start and end are empty, set hasSchool to false
                          const currentStart = schedule[dayKey].start;
                          if ((!newEndValue || newEndValue.trim() === '') && 
                              (!currentStart || currentStart.trim() === '')) {
                            setValue(`schedule.${dayKey}.hasSchool`, false);
                          }
                        }}
                        placeholder="End time"
                        sx={{ 
                          minWidth: 120,
                          '& input:placeholder-shown': {
                            color: 'var(--joy-palette-text-tertiary)'
                          }
                        }}
                      />
                    )}
                  />

                  <Typography>
                    {calculateDuration(
                      schedule[dayKey].start ?? null,
                      schedule[dayKey].end ?? null
                    )}
                  </Typography>

                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={(e) => handleCopyMenuOpen(e, day)}
                    title={`Copy schedule from another day to ${day}`}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Box>

                {/* Second row - Half-day options (only show if global toggle is on and day has school) */}
                {globalSupportsHalf && schedule[dayKey].hasSchool && (
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={2} 
                    sx={{ 
                      pl: 4,
                      py: 1,
                      bgcolor: 'background.level1',
                      borderRadius: 'sm',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Controller
                      control={control}
                      name={`schedule.${dayKey}.supportsHalf` as any}
                      render={({ field }) => (
                        <FormControl sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography component="label" sx={{ display: "flex", alignItems: "center", fontWeight: 500, color: 'primary.500' }}>
                            <Switch
                              {...field}
                              checked={!!field.value}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                field.onChange(isChecked);
                                if (!isChecked) {
                                  setValue(`schedule.${dayKey}.halfEnd` as any, null);
                                } else {
                                  // Set a default half-day end time (e.g., 11:15 PM)
                                  setValue(`schedule.${dayKey}.halfEnd` as any, "11:15");
                                }
                              }}
                              sx={{ mr: 1 }}
                            />
                            Half-Day Option
                          </Typography>
                        </FormControl>
                      )}
                    />

                    {(schedule[dayKey] as any).supportsHalf && (
                      <>
                        <Typography color="primary" sx={{ fontWeight: 500 }}>ends at</Typography>
                        <Controller
                          control={control}
                          name={`schedule.${dayKey}.halfEnd` as any}
                          render={({ field }) => (
                            <Input
                              type="time"
                              value={field.value as string || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              placeholder="Half-day end"
                              sx={{ 
                                minWidth: 120,
                                '& input:placeholder-shown': {
                                  color: 'var(--joy-palette-text-tertiary)'
                                }
                              }}
                            />
                          )}
                        />
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Card>
          );
        })}

        {/* Copy menu */}
        <Menu
          anchorEl={copyMenuAnchor}
          open={Boolean(copyMenuAnchor)}
          onClose={handleCopyMenuClose}
          placement="bottom-start"
          disablePortal
        >
          {(() => {
            const validDays = daysOfWeek
              .filter(day => day !== copyMenuTarget)
              .filter(day => {
                const dayKey = day.toLowerCase() as keyof ClassFormValues["schedule"];
                const daySchedule = schedule[dayKey];
                return daySchedule.hasSchool && daySchedule.start && daySchedule.end;
              });
            if (validDays.length === 0) {
              return (
                <MenuItem disabled>
                  <Typography>No days available to copy from</Typography>
                </MenuItem>
              );
            }
            return validDays.map((day) => {
              const dayKey = day.toLowerCase() as keyof ClassFormValues["schedule"];
              const daySchedule = schedule[dayKey];
              return (
                <MenuItem
                  key={day}
                  onClick={() => handleCopyFromDay(day)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{day}</Typography>
                    <Typography level="body-sm" color="neutral">
                      ({daySchedule.start} - {daySchedule.end})
                    </Typography>
                  </Box>
                </MenuItem>
              );
            });
          })()}
        </Menu>
      </form>
    </Box>
  );
};

export default AddEditClassModal;